import React, { useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

type Props = {
  onToken: (token: string) => void;
  onExpire?: () => void;
  resetKey?: number;
};

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

export const TurnstileWidget: React.FC<Props> = ({
  onToken,
  onExpire,
  resetKey = 0,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onTokenRef.current = onToken;
    onExpireRef.current = onExpire;
  }, [onToken, onExpire]);

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) {
      return undefined;
    }

    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) {
        return;
      }
      if (widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
        widgetIdRef.current = null;
        containerRef.current.innerHTML = '';
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token) => onTokenRef.current(token),
        'expired-callback': () => onExpireRef.current?.(),
        'error-callback': () => onExpireRef.current?.(),
      });
    };

    const existing = document.querySelector(
      'script[data-za-turnstile="1"]',
    ) as HTMLScriptElement | null;

    if (window.turnstile) {
      renderWidget();
    } else if (existing) {
      window.onTurnstileLoad = renderWidget;
    } else {
      window.onTurnstileLoad = renderWidget;
      const script = document.createElement('script');
      script.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.zaTurnstile = '1';
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
        widgetIdRef.current = null;
      }
    };
  }, [resetKey]);

  if (!SITE_KEY) {
    return (
      <div
        style={{
          color: '#b00020',
          fontSize: 14,
          marginTop: 8,
          textAlign: 'center',
        }}
      >
        Captcha não configurado (REACT_APP_TURNSTILE_SITE_KEY).
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        marginTop: 12,
        minHeight: 65,
        display: 'flex',
        justifyContent: 'center',
      }}
    />
  );
};
